import {Button, Icon, IconButton, Stack, Tooltip} from "@chakra-ui/react";
import * as React from "react";

type Props = {
    isExpanded?: boolean;
    onToggleExpand?: () => void;
    onWidenX: () => void;
    onNarrowX: () => void;
    onWidenY: () => void;
    onNarrowY: () => void;
    disableNarrowX?: boolean;
    disableNarrowY?: boolean;
    showImplicitPaths?: boolean;
    onToggleImplicitPaths?: () => void;
};

const ControlButton = ({label, onClick, icon, isDisabled}: {
    label: string;
    onClick: () => void;
    icon: React.ReactElement;
    isDisabled?: boolean;
}) => (
    <Tooltip label={label} hasArrow placement={"left"}>
        <IconButton
            aria-label={label}
            icon={icon}
            size={"sm"}
            variant={"outline"}
            bg={"white"}
            color={"gray.700"}
            borderColor={"gray.200"}
            boxShadow={"sm"}
            _hover={{bg: "gray.50"}}
            onClick={onClick}
            isDisabled={isDisabled}
        />
    </Tooltip>
);

const ExpandIcon = () => (
    <Icon viewBox="0 0 24 24" boxSize={4}>
        <path d="M4 9V4h5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
        <path d="M20 9V4h-5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
        <path d="M4 15v5h5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
        <path d="M20 15v5h-5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
    </Icon>
);

const CollapseIcon = () => (
    <Icon viewBox="0 0 24 24" boxSize={4}>
        <path d="M9 4H4v5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
        <path d="M15 4h5v5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
        <path d="M9 20H4v-5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
        <path d="M15 20h5v-5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
    </Icon>
);

const WidenXIcon = () => (
    <Icon viewBox="0 0 24 24" boxSize={4}>
        <path d="M4 12h16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
        <path d="M7 9L4 12L7 15" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M17 9L20 12L17 15" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    </Icon>
);

const NarrowXIcon = () => (
    <Icon viewBox="0 0 24 24" boxSize={4}>
        <path d="M4 12h7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
        <path d="M15 12h7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
        <path d="M11 12L7 9" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
        <path d="M11 12L7 15" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
        <path d="M15 12L19 9" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
        <path d="M15 12L19 15" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
    </Icon>
);

const WidenYIcon = () => (
    <Icon viewBox="0 0 24 24" boxSize={4}>
        <path d="M12 4v16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
        <path d="M9 7L12 4L15 7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M9 17L12 20L15 17" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    </Icon>
);

const NarrowYIcon = () => (
    <Icon viewBox="0 0 24 24" boxSize={4}>
        <path d="M12 4v7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
        <path d="M12 15v7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
        <path d="M12 11L9 7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
        <path d="M12 11L15 7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
        <path d="M12 15L9 19" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
        <path d="M12 15L15 19" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
    </Icon>
);

export const FitIcon = () => (
    <Icon viewBox="0 0 24 24" boxSize={4}>
        <path d="M4 8V4h4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
        <path d="M20 8V4h-4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
        <path d="M4 16v4h4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
        <path d="M20 16v4h-4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
        <path d="M8 12h8" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
    </Icon>
);

const FlowControls = ({
    isExpanded,
    onToggleExpand,
    onWidenX,
    onNarrowX,
    onWidenY,
    onNarrowY,
    disableNarrowX,
    disableNarrowY,
    showImplicitPaths,
    onToggleImplicitPaths
}: Props) => {
    const implicitLabel = showImplicitPaths ? "Hide implicit paths" : "Show implicit paths";
    const implicitActiveStyles = showImplicitPaths
        ? {
            bg: "#2b6cb0",
            borderColor: "#1a365d",
            color: "white",
            _hover: {bg: "#2c5282"}
        }
        : {};
    return (
        <Stack position={"absolute"} right={3} bottom={3} spacing={2} alignItems={"center"}>
            <Button
                size={"xs"}
                variant={showImplicitPaths ? "solid" : "outline"}
                colorScheme={"blue"}
                onClick={onToggleImplicitPaths}
                {...implicitActiveStyles}
            >
                {implicitLabel}
            </Button>
            <ControlButton label={"Widen horizontally"} onClick={onWidenX} icon={<WidenXIcon/>}/>
            <ControlButton label={"Narrow horizontally"} onClick={onNarrowX} icon={<NarrowXIcon/>} isDisabled={disableNarrowX}/>
            <ControlButton label={"Widen vertically"} onClick={onWidenY} icon={<WidenYIcon/>}/>
            <ControlButton label={"Narrow vertically"} onClick={onNarrowY} icon={<NarrowYIcon/>} isDisabled={disableNarrowY}/>
            {onToggleExpand ? (
                <ControlButton
                    label={isExpanded ? "Collapse" : "Expand"}
                    onClick={onToggleExpand}
                    icon={isExpanded ? <CollapseIcon/> : <ExpandIcon/>}
                />
            ) : null}
        </Stack>
    );
};

export default FlowControls;
